/**
 * @package   ScandiPWA\GtmGraphQl
 * @author    Abdelrahman Hady <abdelrahman.hady@scandiweb.com>
 * @copyright Copyright (c) 2023 Scandiweb, Inc (https://scandiweb.com)
 * @license   http://opensource.org/licenses/OSL-3.0 The Open Software License 3.0 (OSL-3.0)
 */

import { roundPrice } from 'Util/Price';

import Event, { EVENT_GTM_PURCHASE_GA4 } from '../../../util/Event';
import BaseEventEvent from './BaseEvent.event';

export const PURCHASE_EVENT_HANDLE_DELAY = 1200;
export const SPAM_PROTECTION_DELAY = 10000;

/**
 * On order success page "Purchase"
 *
 * @namespace Gtm/Component/GoogleTagManager/GA4_events/Purchase/Event
 */
export class PurchaseEvent extends BaseEventEvent {
    /**
     * Event delay
     *
     * @type {number}
     */
    eventHandleDelay = PURCHASE_EVENT_HANDLE_DELAY;

    /**
     * Bind on product detail
     */
    bindEvent() {
        Event.observer(EVENT_GTM_PURCHASE_GA4, ({ orderID: orderId, totals }) => {
            this.handle(
                orderId,
                totals
            );
        });
    }

    /**
     * Handle
     *
     * @param orderId
     * @param totals
     */
    handler(orderId, totals) {
        if (this.spamProtection(SPAM_PROTECTION_DELAY)) {
            return;
        }

        const {
            payment_type,
            items,
            cashOnDeliveryFee,
            coupon_code = '',
            prices: {
                applied_taxes,
                discount,
                subtotal_including_tax: { value: subtotal },
                shipping_incl_tax
            }
        } = totals;

        const taxAmount = applied_taxes.reduce(
            ({ amount: { value } }, { amount: { value: currentValue } }) => value + currentValue,
            { amount: { value: 0 } }
        );

        const tax = +roundPrice(taxAmount || 0);

        this.pushEventData({
            currency: this.getCurrencyCode(),
            tax,
            coupon: coupon_code,
            value: +roundPrice(subtotal + (cashOnDeliveryFee || 0) - tax),
            shipping: +roundPrice(shipping_incl_tax || 0),
            discount: +roundPrice(Math.abs(discount?.amount?.value || 0)),
            payment_type,
            affiliation: window.location.hostname,
            transaction_id: orderId,
            items: this.getCartItemsWithListData(items)
        });
    }
}

export default PurchaseEvent;
